import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CircleHelp, Download, Loader2, UserCircle } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { exportMobileUsersCsv, getMobileUsers, MobileUser, MobileUsersSort, OnboardingProgress } from '../lib/api/adminMobileUsers'

const USERS_PER_PAGE = 20

const ONBOARDING_PROGRESS_GUIDE = [
  { progress: 'NotStarted', percent: 0, milestone: 'Not started', nextArea: 'Welcome' },
  { progress: 'IntroSlidesSeen', percent: 6, milestone: 'Intro seen', nextArea: 'Welcome' },
  { progress: 'AccountCreated', percent: 12, milestone: 'Account created', nextArea: 'Username' },
  { progress: 'UsernameCompleted', percent: 19, milestone: 'Username completed', nextArea: 'Age' },
  { progress: 'AgeCompleted', percent: 25, milestone: 'Age completed', nextArea: 'Gender, then Hiking Frequency' },
  { progress: 'HikingFrequencyCompleted', percent: 31, milestone: 'Hiking Frequency completed', nextArea: 'Endurance' },
  { progress: 'EnduranceCompleted', percent: 38, milestone: 'Endurance completed', nextArea: 'Training Days' },
  { progress: 'AvailabilityCompleted', percent: 44, milestone: 'Availability completed', nextArea: 'Dormant/older checkpoint; Training Days' },
  { progress: 'TrainingDaysCompleted', percent: 50, milestone: 'Training Days completed', nextArea: 'Equipment' },
  { progress: 'EquipmentCompleted', percent: 56, milestone: 'Equipment completed', nextArea: 'Units' },
  { progress: 'UnitsCompleted', percent: 62, milestone: 'Units completed', nextArea: 'Choose Goal and goal-selection subflows' },
  { progress: 'GoalSelected', percent: 69, milestone: 'Goal selected', nextArea: 'Dormant/older checkpoint; Building Plan' },
  { progress: 'PackWeightCompleted', percent: 75, milestone: 'Pack Weight completed', nextArea: 'Hike Date' },
  { progress: 'HikeDateCompleted', percent: 81, milestone: 'Hike Date completed', nextArea: 'Concerns' },
  { progress: 'ConcernsCompleted', percent: 88, milestone: 'Concerns completed', nextArea: 'Building Plan' },
  { progress: 'PlanGenerated', percent: 94, milestone: 'Plan generated', nextArea: 'Plan Overview, subscription, welcome, or Home handoff' },
  { progress: 'OnboardingCompleted', percent: 100, milestone: 'Onboarding completed', nextArea: 'Home' }
] as const satisfies ReadonlyArray<{
  progress: OnboardingProgress
  percent: number
  milestone: string
  nextArea: string
}>

const ONBOARDING_PROGRESS_STEPS: OnboardingProgress[] = ONBOARDING_PROGRESS_GUIDE.map((step) => step.progress)

const normalizeOnboardingProgress = (progress?: OnboardingProgress | null) => {
  if (!progress) return null

  const legacyMap: Record<string, OnboardingProgress> = {
    GymAccessCompleted: 'EquipmentCompleted',
    TrueElevationCompleted: 'EquipmentCompleted',
    PreferredDaysCompleted: 'TrainingDaysCompleted',
    SummaryReviewed: 'ConcernsCompleted'
  }

  return legacyMap[progress] ?? progress
}

type OnboardingFilter = 'all' | 'incomplete'

type AdminMobileUser = MobileUser & {
  onboardingCompletionPercent: number
}

type SelectedOnboardingProgress = {
  displayName: string
  percent: number
}

const formatLoginType = (value?: MobileUser['loginType'] | null) => {
  switch (value) {
    case 'Apple':
      return 'Apple'
    case 'Google':
      return 'Google'
    case 'Facebook':
      return 'Facebook'
    case 'EmailPassword':
    default:
      return 'Email/password'
  }
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—'
  }
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(value))
  } catch (error) {
    console.warn('Failed to format date', error)
    return value
  }
}

const deriveOnboardingProgress = (user: MobileUser) => {
  if (typeof user.onboardingProgressPercent === 'number') {
    return Math.min(100, Math.max(0, Math.round(user.onboardingProgressPercent)))
  }

  const normalized = normalizeOnboardingProgress(user.onboardingProgress)
  if (normalized && (normalized !== 'NotStarted' || !user.onboardingData)) {
    const stepIndex = ONBOARDING_PROGRESS_STEPS.indexOf(normalized)
    if (stepIndex >= 0) {
      const percent = Math.round((stepIndex / (ONBOARDING_PROGRESS_STEPS.length - 1)) * 100)
      return Math.min(100, Math.max(0, percent))
    }
  }

  const onboarding = user.onboardingData
  if (!onboarding) {
    return 0
  }
  const checkpoints = [
    onboarding.ageRange,
    onboarding.trainingDaysPerWeek,
    onboarding.preferredDays?.length ? 'days' : null,
    onboarding.enduranceFrequency,
    onboarding.hikingFrequency,
    onboarding.measurementUnit,
    onboarding.trueElevationWorkouts?.length ? 'workouts' : null,
    onboarding.equipmentAccess?.length ? 'equipment' : null,
    onboarding.packWeightOption,
    onboarding.hikeDate ?? (onboarding.hikeDateUnknown ? 'tbd' : null),
    onboarding.concerns?.length || onboarding.otherConcerns ? 'concerns' : null
  ]

  const completed = checkpoints.filter(Boolean).length
  const percent = Math.round((completed / checkpoints.length) * 100)
  return Math.min(100, Math.max(0, percent))
}

const subscriptionBadgeClass = (isSubscriber?: boolean | null) =>
  isSubscriber ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'

const getUserDisplayName = (user: MobileUser) => user.username ?? user.email ?? user.userId

const MobileUserAvatar = ({ user }: { user: MobileUser }) => {
  const displayName = getUserDisplayName(user)

  if (user.profileImageUrl) {
    return (
      <img
        src={user.profileImageUrl}
        alt={`${displayName} profile`}
        className="h-9 w-9 shrink-0 rounded-full border border-border object-cover"
      />
    )
  }

  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-muted-foreground">
      <UserCircle className="h-5 w-5" aria-hidden="true" />
    </div>
  )
}

export const MobileUsersPage = () => {
  const navigate = useNavigate()
  const requestSequence = useRef(0)
  const [users, setUsers] = useState<MobileUser[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [onboardingFilter, setOnboardingFilter] = useState<OnboardingFilter>('all')
  const [sortBy, setSortBy] = useState<MobileUsersSort>('recent')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [filteredTotal, setFilteredTotal] = useState(0)
  const [paidTotal, setPaidTotal] = useState(0)
  const [onboardingIncompleteTotal, setOnboardingIncompleteTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [selectedOnboardingProgress, setSelectedOnboardingProgress] = useState<SelectedOnboardingProgress | null>(null)

  const loadUsers = useCallback(async () => {
    const requestId = ++requestSequence.current
    setLoading(true)
    setError(null)
    try {
      const response = await getMobileUsers({
        skip: (currentPage - 1) * USERS_PER_PAGE,
        limit: USERS_PER_PAGE,
        search: debouncedSearchTerm || undefined,
        sortBy,
        onboardingIncomplete: onboardingFilter === 'incomplete'
      })
      if (requestId !== requestSequence.current) {
        return
      }
      setUsers(response.users)
      setTotalUsers(response.total)
      setFilteredTotal(response.filteredTotal)
      setPaidTotal(response.paidTotal)
      setOnboardingIncompleteTotal(response.onboardingIncompleteTotal)
    } catch (err) {
      if (requestId !== requestSequence.current) {
        return
      }
      console.error('Failed to load mobile users', err)
      setError('Unable to load mobile users right now.')
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false)
      }
    }
  }, [currentPage, debouncedSearchTerm, onboardingFilter, sortBy])

  useEffect(() => {
    void loadUsers()
  }, [loadUsers])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim())
    }, 250)
    return () => window.clearTimeout(timeout)
  }, [searchTerm])

  const pagedUsers: AdminMobileUser[] = users.map((user) => ({
    ...user,
    onboardingCompletionPercent: deriveOnboardingProgress(user)
  }))
  const metrics = {
    total: totalUsers,
    paidRate: totalUsers > 0 ? Math.round((paidTotal / totalUsers) * 100) : 0,
    incomplete: onboardingIncompleteTotal
  }
  const totalPages = Math.max(1, Math.ceil(filteredTotal / USERS_PER_PAGE))
  const activePage = Math.min(currentPage, totalPages)
  const pageStartIndex = filteredTotal > 0 ? (activePage - 1) * USERS_PER_PAGE : 0
  const pageEndIndex = pageStartIndex + pagedUsers.length

  useEffect(() => {
    if (!loading && currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, loading, totalPages])

  const selectedGuideStep = selectedOnboardingProgress
    ? ONBOARDING_PROGRESS_GUIDE.find((step) => step.percent === selectedOnboardingProgress.percent)
    : undefined

  const handleManageUser = (userId: string) => {
    navigate(`/users/mobile/${encodeURIComponent(userId)}`)
  }

  const handleExportUsers = async () => {
    setExporting(true)
    setExportError(null)
    try {
      const blob = await exportMobileUsersCsv()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `hikestrong-mobile-users-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 0)
    } catch (err) {
      console.error('Failed to export mobile users', err)
      setExportError('Unable to download the mobile user CSV right now.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <header className="workspace-heading">
        <div><h1>Mobile users</h1><p>Review app accounts, paid access, and onboarding progress.</p></div>
      </header>
      <dl aria-label="Mobile account summary" className="workspace-summary grid-cols-2 sm:grid-cols-3">
        <div><dt>Total mobile users</dt><dd>{loading || error ? '—' : metrics.total}</dd><p className="summary-detail">Across the HikeStrong app</p></div>
        <div><dt>Paid subscriptions</dt><dd>{loading || error ? '—' : metrics.paidRate + '%'}</dd><p className="summary-detail">Current paid access</p></div>
        <div><dt>Needs onboarding help</dt><dd>{loading || error ? '—' : metrics.incomplete}</dd><p className="summary-detail">Progress below 80%</p></div>
      </dl>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>People insights</CardTitle>
              <CardDescription>Search accounts, sort by paid access, or focus on onboarding gaps.</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button className="gap-2" variant="outline" size="sm" onClick={() => void handleExportUsers()} disabled={exporting} type="button">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
                {exporting ? 'Preparing CSV…' : 'Download users CSV'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => loadUsers()} disabled={loading} type="button">
                Refresh data
              </Button>
              <Button
                variant={onboardingFilter === 'incomplete' ? 'default' : 'secondary'}
                size="sm"
                onClick={() => {
                  setOnboardingFilter((filter) => filter === 'incomplete' ? 'all' : 'incomplete')
                  setCurrentPage(1)
                }}
                aria-pressed={onboardingFilter === 'incomplete'}
                type="button"
              >
                {onboardingFilter === 'incomplete' ? 'Show all users' : 'Highlight onboarding gaps'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <Input
              aria-label="Search mobile users" placeholder="Search by username, email, or ID"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value)
                setCurrentPage(1)
              }}
              className="xl:w-80"
            />

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                Sort by
                <select
                  value={sortBy}
                  onChange={(event) => {
                    setSortBy(event.target.value as MobileUsersSort)
                    setCurrentPage(1)
                  }}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="recent">Recent activity</option>
                  <option value="paid">Paid first</option>
                  <option value="free">Free first</option>
                  <option value="oldest">Oldest signup</option>
                  <option value="progress">Progress (low first)</option>
                </select>
              </label>
            </div>
          </div>

          {loading && <p className="text-sm text-muted-foreground">Loading mobile users…</p>}
          {error && <p className="text-sm text-red-500">{error}</p>}
          {exportError && <p className="text-sm text-red-500">{exportError}</p>}

          <div className="overflow-x-auto">
            <table className="workspace-table min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">User</th>
                  <th className="py-2 pr-4 font-medium">Login type</th>
                  <th className="py-2 pr-4 font-medium">Subscription</th>
                  <th className="py-2 pr-4 font-medium">Onboarding</th>
                  <th className="py-2 pr-4 font-medium">Last activity</th>
                  <th className="py-2 pr-4 font-medium">Manage</th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.map((user) => (
                  <tr key={user.userId} className="border-b border-border/60 last:border-b-0 hover:bg-muted/30">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <MobileUserAvatar user={user} />
                        <div>
                          <p className="font-medium text-foreground">{getUserDisplayName(user)}</p>
                          {user.email && <p className="text-xs text-muted-foreground">{user.email}</p>}
                          <p className="text-xs text-muted-foreground">{user.userId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {formatLoginType(user.loginType)}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${subscriptionBadgeClass(user.isSubscriber)}`}>
                        {user.isSubscriber ? 'Paid' : 'Free'}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <button
                        type="button"
                        onClick={() => setSelectedOnboardingProgress({
                          displayName: getUserDisplayName(user),
                          percent: user.onboardingCompletionPercent
                        })}
                        className="group flex min-w-32 flex-col gap-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        aria-label={`Explain ${user.onboardingCompletionPercent}% onboarding progress for ${getUserDisplayName(user)}`}
                        title="Open onboarding progress guide"
                      >
                        <div className="h-2 w-full rounded-full bg-muted/40">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${user.onboardingCompletionPercent}%` }}
                          />
                        </div>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground group-hover:underline">
                          {user.onboardingCompletionPercent}% complete
                          <CircleHelp className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                      </button>
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground">{formatDate(user.lastLoginAt)}</td>
                    <td className="py-3 pr-4">
                      <Button variant="ghost" size="sm" onClick={() => handleManageUser(user.userId)} type="button">
                        Manage
                      </Button>
                    </td>
                  </tr>
                ))}
                {!loading && filteredTotal === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      No mobile users match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!loading && filteredTotal > 0 && (
            <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {pageStartIndex + 1}-{pageEndIndex} of {filteredTotal} profiles
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={activePage <= 1}
                  type="button"
                >
                  Previous
                </Button>
                <span className="min-w-24 text-center text-sm text-muted-foreground">
                  Page {activePage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={activePage >= totalPages}
                  type="button"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={selectedOnboardingProgress !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedOnboardingProgress(null)
          }
        }}
      >
        <DialogContent className="w-[min(96vw,920px)] p-5 sm:p-7">
          <div className="space-y-2">
            <DialogTitle>Onboarding progress guide</DialogTitle>
            <DialogDescription>
              Percentages represent the last persisted milestone, not always the exact screen currently open. Some screens share a checkpoint.
            </DialogDescription>
          </div>

          {selectedOnboardingProgress && (
            <div className="mt-5 rounded-xl border border-primary/25 bg-primary/5 p-4" data-testid="selected-onboarding-progress">
              <p className="text-sm font-semibold text-foreground">
                {selectedOnboardingProgress.displayName} is at {selectedOnboardingProgress.percent}%
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedGuideStep
                  ? `${selectedGuideStep.milestone}. Next/current area: ${selectedGuideStep.nextArea}.`
                  : 'This is a field-derived estimate rather than one of the standard persisted milestone percentages.'}
              </p>
            </div>
          )}

          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Percent</th>
                  <th className="px-4 py-3 font-medium">Last completed milestone</th>
                  <th className="px-4 py-3 font-medium">Next/current area</th>
                </tr>
              </thead>
              <tbody>
                {ONBOARDING_PROGRESS_GUIDE.map((step) => {
                  const isSelected = selectedOnboardingProgress?.percent === step.percent
                  return (
                    <tr
                      key={step.percent}
                      className={`border-t border-border ${isSelected ? 'bg-primary/10' : ''}`}
                      data-selected={isSelected ? 'true' : undefined}
                    >
                      <td className="whitespace-nowrap px-4 py-3 font-semibold text-foreground">{step.percent}%</td>
                      <td className="px-4 py-3 text-foreground">{step.milestone}</td>
                      <td className="px-4 py-3 text-muted-foreground">{step.nextArea}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-5 rounded-lg bg-muted/30 p-3 text-xs text-muted-foreground">
            The unusual values come from dividing 16 intervals across 17 backend progress states and rounding. The progress bar inside the mobile onboarding flow uses a different calculation.
          </div>

          <div className="mt-5 flex justify-end">
            <DialogClose asChild>
              <Button type="button" variant="outline">Close</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
